import getRemoteMetricKey from 'common/src/modules/analytics/utils/getRemoteMetricKey';
import {AvailableDimension} from 'common/src/modules/campaigns/store/insightsTab/insightsTab.types';
import {ToolExecuteParams, ToolResult} from 'common/src/modules/shared/copilot/copilot.types';
import tool from 'common/src/modules/shared/copilot/utils/creators/tool';
import toolResult from 'common/src/modules/shared/copilot/utils/creators/toolResult';
import toolError from 'common/src/modules/shared/copilot/utils/toolError';
import {TimezoneObject} from 'common/src/modules/timezones/timezones.types';
import forceTimeZone from 'common/src/modules/timezones/utils/forceTimeZone';
import {CLStore} from 'common/src/redux/store';
import getMatchingEnumValue from 'common/src/utils/typescriptUtils/getMatchingEnumValue';
import _find from 'lodash/find';
import _map from 'lodash/map';
import {DateTime} from 'luxon';
import analyticsApi from '../../../../analytics.api';
import {
  AiAnalyticsEventType,
  AnalyticsEventType,
  DimensionValue,
  MetricValue,
} from '../../../../analytics.types';
import getRemoteDimensionKey from '../../../../utils/getRemoteDimensionKey';
import {AccountAnalyticsToolName} from '../accountAnalyticsAgent.enums';
import {accountAnalyticsPerformingInputSchema} from '../accountAnalyticsAgent.schemas';
import partitionByEventType from '../utils/partitionByEventType';
import {FeatureFlagKey} from 'common/src/modules/featureFlags/featureFlags.types';
import isFlagEnabled from 'common/src/modules/featureFlags/utils/isFlagEnabled';
import persistInlineWidgetToAccountDashboard from '../utils/persistInlineWidgetToAccountDashboard';

function accountTopPerformingTool(store: CLStore) {
  function handleNoAnalyticsData() {
    return toolError('Campaign data is unavailable, please try again later');
  }

  async function execute({
    input: {
      dimension,
      metrics: aiMetrics,
      startDate,
      endDate,
      count,
      filter,
      eventType: aiEventType,
      type,
    },
    toolUtils,
    callChain,
  }: ToolExecuteParams<typeof accountAnalyticsPerformingInputSchema>): Promise<ToolResult> {
    const {
      dashboardPage: {
        startDate: startDateFromStore,
        endDate: endDateFromStore,
        timezone,
        metricsAndDimensionsOptions: {dimensions, metrics: metricsOptions},
        filters,
        timezoneOptions,
      },
    } = store.getState();

    const metrics = _map(aiMetrics, (metric) =>
      getRemoteMetricKey(metricsOptions, metric),
    ) as MetricValue[];

    const timezoneObject = _find(timezoneOptions, {
      abbr: timezone,
    }) as TimezoneObject;

    const dateRange = {
      startDate: forceTimeZone(
        startDate ? DateTime.fromISO(startDate).toUTC() : DateTime.fromISO(startDateFromStore),
        0,
      ),

      endDate: forceTimeZone(
        endDate ? DateTime.fromISO(endDate).toUTC() : DateTime.fromISO(endDateFromStore),
        0,
      ).endOf('day'),
    };

    const eventType = getMatchingEnumValue(
      AiAnalyticsEventType,
      AnalyticsEventType,
      aiEventType,
    ) as unknown as AnalyticsEventType;

    const [availableDimensions] = partitionByEventType(
      dimensions,
      eventType,
    ) as unknown as AvailableDimension[][];

    const dimensionKey =
      getRemoteDimensionKey<DimensionValue>(availableDimensions, dimension) ?? DimensionValue.Date;

    if (!dimensionKey) {
      return toolError('This dimension is not supported');
    }

    const filterKey = getRemoteDimensionKey<DimensionValue>(
      filters as unknown as AvailableDimension[],
      filter?.dimension,
    );

    const allFilters = [
      ...(filterKey
        ? [
            {
              filterBy: filterKey,
              allValues: filter?.values ?? [],
            },
          ]
        : []),
    ];

    const analyticsData = await analyticsApi.getTopPerformingData({
      dimensions: [dimensionKey],
      filters: allFilters,
      metrics,
      dateRange,
      selectedTimezone: timezoneObject,
      eventType,
      ...(count ? {count} : {}),
    });

    if (!analyticsData.items.length) {
      return handleNoAnalyticsData();
    }

    const isSkillsMode = isFlagEnabled(FeatureFlagKey.EnableCopilotSkills);

    if (!isSkillsMode) {
      return toolResult(JSON.stringify(analyticsData.items));
    }

    return persistInlineWidgetToAccountDashboard({
      toolUtils,
      callChain,
      title: 'Account Top Performing',
      requestedMaxRows: count,
      params: {
        metrics,
        analyticsData: analyticsData.items,
        dimensions: [dimensionKey],
        startDate: dateRange.startDate.toISO(),
        endDate: dateRange.endDate.toISO(),
        type,
        breakdownByTime: false,
      },
    });
  }

  return tool({
    name: AccountAnalyticsToolName.AccountTopPerforming,
    displayName: 'Loading Analytics Data',
    description:
      'Retrieves the campaign "best-performing" dimension, according to metrics (KPIs). Example: "Top-performing creatives by CTR"',
    execute,
    inputSchema: accountAnalyticsPerformingInputSchema,
  });
}

export default accountTopPerformingTool;
